/**
 * Logger Utility with Sentry Integration
 *
 * - Development: Logs to browser console
 * - Production: Sends directly to Sentry Logs (no console output)
 *
 * Use logger.captureError() for Error objects to get full stack traces in Sentry.
 */

import * as Sentry from '@sentry/react';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const isDev = import.meta.env.DEV;
const sentryEnabled = !!import.meta.env.VITE_SENTRY_DSN;

function log(level: LogLevel, message: string, context?: Record<string, unknown>) {
  if (isDev) {
    // Development: log to console
    const consoleFn = level === 'debug' ? 'log' : level;
    console[consoleFn](`[${level.toUpperCase()}]`, message, context || '');
  } else if (sentryEnabled) {
    // Production: send directly to Sentry Logs (no console output)
    const sentryLogger = Sentry.logger;
    const logFn = sentryLogger[level] as (message: string, attrs?: Record<string, unknown>) => void;
    if (logFn) {
      logFn.call(sentryLogger, message, context);
    }

    // Additionally capture errors as Sentry events (for alerting)
    if (level === 'error') {
      Sentry.captureMessage(message, {
        level: 'error',
        extra: context,
      });
    }
  }
  // Production without Sentry: silent
}

export const logger = {
  /**
   * Log debug message (appears in Sentry Logs in production)
   */
  debug: (msg: string, ctx?: Record<string, unknown>) => log('debug', msg, ctx),

  /**
   * Log info message (appears in Sentry Logs in production)
   */
  info: (msg: string, ctx?: Record<string, unknown>) => log('info', msg, ctx),

  /**
   * Log warning message (appears in Sentry Logs in production)
   */
  warn: (msg: string, ctx?: Record<string, unknown>) => log('warn', msg, ctx),

  /**
   * Log error message (appears in Sentry Logs + creates Sentry event in production)
   */
  error: (msg: string, ctx?: Record<string, unknown>) => log('error', msg, ctx),

  /**
   * Capture an actual Error object with full stack trace.
   * Use this instead of logger.error() when you have an Error instance.
   */
  captureError: (error: Error, context?: Record<string, unknown>) => {
    if (isDev) {
      console.error('[ERROR]', error.message, error, context || '');
    } else if (sentryEnabled) {
      // Send to Sentry Logs
      Sentry.logger.error(error.message, { ...context, stack: error.stack });
    }
    // Always capture as Sentry exception for full stack trace and alerting
    if (sentryEnabled) {
      Sentry.captureException(error, { extra: context });
    }
  },

  /**
   * Legacy console-style log (for backwards compatibility)
   * Only logs in development
   */
  log: (...args: unknown[]) => {
    if (isDev) {
      console.log(...args);
    }
  },
};

export default logger;
